import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Package, Plus, Pencil, Star } from "lucide-react";
import api from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { adminMenu } from "@/config/menus";
import { PageHeader } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { StatusPill, LoadingScreen, inr } from "@/components/common";

const EMPTY = { code: "", name: "", price: 0, currency: "INR", billing_period: "monthly", description: "", features: "", benefits: "", highlight: false, status: "ACTIVE" };

export default function AdminPackages() {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null); // package being created/edited (form state)
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/packages").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const openNew = () => setEdit({ ...EMPTY, _new: true });
  const openEdit = (p) => setEdit({ ...p, features: (p.features || []).join("\n"), benefits: (p.benefits || []).join("\n") });

  const save = async () => {
    if (!edit.name?.trim()) { toast.error("Package name is required"); return; }
    setSaving(true);
    const payload = {
      code: edit.code || edit.name, name: edit.name, price: Number(edit.price) || 0,
      currency: edit.currency || "INR", billing_period: edit.billing_period || "monthly",
      description: edit.description || "", highlight: !!edit.highlight, status: edit.status || "ACTIVE",
      features: String(edit.features || "").split("\n").map((s) => s.trim()).filter(Boolean),
      benefits: String(edit.benefits || "").split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (edit._new) await api.post("/admin/packages", payload);
      else await api.put(`/admin/packages/${edit.id}`, payload);
      toast.success(edit._new ? "Package created" : "Package updated");
      setEdit(null); load();
    } catch (e) { if (!e.isAuthError) toast.error(e?.response?.data?.detail || "Could not save package"); }
    finally { setSaving(false); }
  };

  const setStatus = async (p, status) => {
    try { const { data } = await api.post(`/admin/packages/${p.id}/status`, { status }); toast.success(`${p.name} → ${data.status}`); load(); }
    catch (e) { if (!e.isAuthError) toast.error("Could not update status"); }
  };

  return (
    <DashboardShell menu={adminMenu} title="Packages">
      <PageHeader title="Package Master" subtitle="Create and manage subscription packages. Changes apply to new assignments; existing subscriptions keep their original price."
        action={<Button className="bg-brand hover:bg-brand-600" onClick={openNew} data-testid="new-package-btn"><Plus size={16} className="mr-1" /> New Package</Button>} />
      {!rows ? <LoadingScreen /> : (
        <div className="bg-white border border-navy-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-navy-50 text-navy-500 text-xs uppercase"><tr><th className="text-left p-3">Package</th><th className="text-left p-3">Code</th><th className="p-3">Price</th><th className="p-3">Billing</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr></thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-t border-navy-100" data-testid={`package-row-${p.code}`}>
                  <td className="p-3 font-medium text-navy-900">{p.name} {p.highlight && <Star size={13} className="inline text-amber-500 fill-amber-500" />}</td>
                  <td className="p-3 text-navy-500 font-mono text-xs">{p.code}</td>
                  <td className="p-3 text-center text-navy-800">{p.price === 0 ? "Free" : inr(p.price)}</td>
                  <td className="p-3 text-center text-navy-600">{p.billing_period}</td>
                  <td className="p-3 text-center"><StatusPill status={p.status} /></td>
                  <td className="p-3 text-center whitespace-nowrap">
                    <Button size="sm" variant="outline" className="border-navy-300 mr-2" onClick={() => openEdit(p)} data-testid={`edit-package-${p.code}`}><Pencil size={13} className="mr-1" /> Edit</Button>
                    {p.status === "ACTIVE"
                      ? <Button size="sm" variant="outline" className="border-amber-300 text-amber-700" onClick={() => setStatus(p, "INACTIVE")} data-testid={`deactivate-package-${p.code}`}>Deactivate</Button>
                      : <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700" onClick={() => setStatus(p, "ACTIVE")} data-testid={`activate-package-${p.code}`}>Activate</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="package-form-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Package size={18} className="text-brand" /> {edit?._new ? "Create Package" : "Edit Package"}</DialogTitle>
            <DialogDescription>Display name, price and features all come from here — no code changes needed.</DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Package Name</Label><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="pkg-name" placeholder="e.g. BUSINESS" /></div>
                <div><Label className="text-xs">Code / Slug</Label><Input value={edit.code} onChange={(e) => setEdit({ ...edit, code: e.target.value })} disabled={!edit._new} data-testid="pkg-code" placeholder="auto from name" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">Price</Label><Input type="number" value={edit.price} onChange={(e) => setEdit({ ...edit, price: e.target.value })} data-testid="pkg-price" /></div>
                <div><Label className="text-xs">Currency</Label><Input value={edit.currency} onChange={(e) => setEdit({ ...edit, currency: e.target.value })} data-testid="pkg-currency" /></div>
                <div>
                  <Label className="text-xs">Billing</Label>
                  <Select value={edit.billing_period} onValueChange={(v) => setEdit({ ...edit, billing_period: v })}>
                    <SelectTrigger data-testid="pkg-billing"><SelectValue /></SelectTrigger>
                    <SelectContent>{["monthly", "quarterly", "yearly", "one-time"].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="text-xs">Description</Label><Input value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} data-testid="pkg-desc" /></div>
              <div><Label className="text-xs">Features (one per line)</Label><Textarea rows={4} value={edit.features} onChange={(e) => setEdit({ ...edit, features: e.target.value })} data-testid="pkg-features" /></div>
              <div><Label className="text-xs">Benefits (one per line, optional)</Label><Textarea rows={2} value={edit.benefits} onChange={(e) => setEdit({ ...edit, benefits: e.target.value })} data-testid="pkg-benefits" /></div>
              <div className="flex items-center gap-3"><Switch checked={!!edit.highlight} onCheckedChange={(v) => setEdit({ ...edit, highlight: v })} data-testid="pkg-highlight" /><Label className="text-xs">Highlight as "Most Popular" on pricing page</Label></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-600" onClick={save} disabled={saving} data-testid="save-package-btn">{saving ? "Saving…" : "Save Package"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
